import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DirectUpload } from "@rails/activestorage";
import { useTranslation } from "react-i18next";

// Icons
import { FaTrash, FaUser } from "react-icons/fa";

// Components
import { UploadFile } from "../../../../types";
import Spinner from "../../../../components/spinner";

// Styles
import styles from './avatarOption.module.scss';


interface AvatarOptionProps {
  selected?: boolean;
  avatarSrc: string | null;
  userUploadedAvatar?: boolean;
  onUserUploadedAvatar?: (avatarSrc: string | null) => void;
  onSelect?: () => void;
}

const AvatarOption: React.FC<AvatarOptionProps> = ({ avatarSrc, userUploadedAvatar = false, onSelect, selected = false, onUserUploadedAvatar }) => {
  // Translations
  const { t } = useTranslation();

  // State
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [avatar, setAvatar] = useState<string | null>(avatarSrc);
  const [uploadedAvatar, setUploadedAvatar] = useState<UploadFile | null>(null);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Memoized values

  // Methods
  const uploadAvatar = async (file: File) => {
    if (!file) return;

    setIsUploading(true);

    const directUpload = new DirectUpload(file, "/rails/active_storage/direct_uploads");

    directUpload.create((error, attributes) => {
      if (error) console.error(error);
      else {
        const url = `/rails/active_storage/blobs/redirect/${attributes?.signed_id}/${encodeURIComponent(attributes?.filename || "")}`;
        setUploadedAvatar({ ...attributes, url, signed_id: attributes?.signed_id || "" });
        onUserUploadedAvatar?.(url);
        setAvatar(url);
      }
      setIsUploading(false);
    });
  }

  useEffect(() => {
    setAvatar(avatarSrc);
  }, [avatarSrc]);

  // Handlers
  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadAvatar(file);
  }

  const handleDeleteAvatar = useCallback(() => {
    setUploadedAvatar(null);
    setAvatar(null);
    onUserUploadedAvatar?.(null);
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length === 0) return;

    const file = e.dataTransfer.files?.[0];
    if (file) uploadAvatar(file);
  }
  // Styles
  const rootClasses = "relative flex flex-col flex-shrink-0 items-center justify-center gap-2 rounded-full w-[120px] h-[120px]";

  // Renderers
  const renderAvatar = useCallback(() => {
    let avatarClasses = "w-[100px] h-[100px] rounded-full bg-purple-100";
    if (selected) avatarClasses += " ring-2 ring-blue-500 w-[120px] h-[120px]";
    if (isDragging) avatarClasses += ` bg-purple-200`;

    if (avatar) return <img src={avatar} alt="Avatar" className={avatarClasses} />;
    return (
      <div
        className={`${avatarClasses} flex flex-col items-center justify-center gap-2 p-2 cursor-pointer`}
        onClick={userUploadedAvatar ? () => fileInputRef.current?.click() : undefined}
        onDrop={userUploadedAvatar ? handleDrop : undefined}
        onDragOver={userUploadedAvatar ? handleDragOver : undefined}
        onDragLeave={userUploadedAvatar ? handleDragLeave : undefined}
      >
        <FaUser className="text-purple-500 text-2xl" />

        <span className="text-purple-500 text-sm text-center break-words">
          {t('profile.click_to_upload_or_drag')}
        </span>
      </div>
    )
  }, [avatar, isDragging, selected]);

  return (
    <div
      className={rootClasses}
      onClick={onSelect ? () => onSelect() : undefined}
    >
      {isUploading && (
        <div className="absolute top-0 start-0 w-full h-full flex items-center justify-center overflow-hidden rounded-full">
          <Spinner />
        </div>
      )}

      {renderAvatar()}

      {userUploadedAvatar && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
        />
      )}

      {(!!uploadedAvatar || !!avatar) && userUploadedAvatar && (
        <div className="absolute bottom-0 end-0 z-40">
          <button
            className="text-white text-sm bg-white rounded-full p-1 hover:scale-[1.02] transition-all duration-300"
            onClick={handleDeleteAvatar}
          >
            <FaTrash className="text-red-500 text-md" />
          </button>
        </div>
      )}
    </div>
  )
}

export default AvatarOption;
